import React, { useEffect, useState } from 'react';
import './MacroIssuesTable.css';

function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function typeLabel(type) {
  if (type === 'worker_gap') return 'Worker Gap';
  if (type === 'supply_block') return 'Supply Block';
  return type;
}

/**
 * Macro card — longest Supply Blocks and Worker Gaps from /api/replay_facts.
 * No derivation in the browser; rows seek the Playhead.
 */
const MacroCard = ({ apiBaseUrl, replayId, player, top = 5, onSeek }) => {
  const [state, setState] = useState({ status: 'idle', issues: [], error: '' });

  useEffect(() => {
    if (!apiBaseUrl || !replayId || !player) {
      setState({ status: 'empty', issues: [], error: '' });
      return undefined;
    }
    let cancelled = false;
    setState({ status: 'loading', issues: [], error: '' });
    const url =
      `${apiBaseUrl}/replay_facts/${encodeURIComponent(replayId)}` +
      `/macro_issues?player=${encodeURIComponent(player)}&top=${top}`;
    fetch(url)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        const issues = Array.isArray(data?.issues) ? data.issues : [];
        setState({
          status: issues.length === 0 ? 'empty' : 'ready',
          issues,
          error: '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: 'error',
          issues: [],
          error: err?.message || 'Request failed',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, replayId, player, top]);

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="macro-issues" data-testid="macro-card-body">
        <p className="macro-issues-empty">Loading Macro Issues…</p>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="macro-issues" data-testid="macro-card-body">
        <p className="macro-issues-empty" data-testid="macro-card-error">
          Could not load Macro Issues ({state.error}).
        </p>
      </div>
    );
  }
  if (state.status === 'empty') {
    return (
      <div className="macro-issues" data-testid="macro-card-body">
        <p className="macro-issues-empty">No Worker Gaps or Supply Blocks in the top list.</p>
      </div>
    );
  }

  return (
    <div className="macro-issues" data-testid="macro-card-body">
      <table className="macro-issues-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Duration</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {state.issues.map((issue, index) => (
            <tr
              key={`${issue.type}-${issue.start_real_time}-${index}`}
              className="macro-issues-row"
              data-testid="macro-card-row"
              onClick={() => onSeek?.(issue.start_real_time)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSeek?.(issue.start_real_time);
                }
              }}
              tabIndex={0}
              role="button"
            >
              <td>{typeLabel(issue.type)}</td>
              <td>
                <span className="macro-issues-dur">{Math.round(issue.duration)}s</span>{' '}
                <span className="macro-issues-range">
                  ({formatClock(issue.start_real_time)}–{formatClock(issue.end_real_time)})
                </span>
              </td>
              <td>{issue.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="macro-issues-hint">Click a row to seek the Playhead and pause.</p>
    </div>
  );
};

export default MacroCard;
